/**
 * Central HTTP-status -> human copy mapper for OCPP endpoints.
 *
 * Mirrors the table in `OCPP_FRONTEND_CHANGES_PHASE2.md` §5 so behaviour
 * is consistent across every tab. Use it from any service or hook so we
 * don't reimplement the "what does 502 mean" branch logic per surface.
 */

import type { OcppErrorBody, OcppGateBlock } from '../models';

export interface OcppApiErrorInfo {
  /** Short label suitable for a toast / banner title. */
  title: string;
  /** Sentence-length explanation suitable for a banner body. */
  message: string;
  /** True when the action was blocked by the connector-state gate (Phase E). */
  blockedByGate: boolean;
  /** When `blockedByGate` is true, the parsed gate body if available. */
  gate?: OcppGateBlock['gate'];
  /** True when the feature is gated behind an env var (rotate). */
  featureGated: boolean;
  /** Raw status passed to `mapOcppApiError` (or undefined for network errors). */
  status: number | undefined;
}

const isGateBlock = (payload: unknown): payload is OcppGateBlock => {
  if (!payload || typeof payload !== 'object') return false;
  const p = payload as { gate?: { allowed?: unknown; reason?: unknown; status?: unknown } };
  return (
    !!p.gate &&
    typeof p.gate === 'object' &&
    p.gate.allowed === false &&
    typeof p.gate.reason === 'string'
  );
};

const readMessage = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;
  const p = payload as OcppErrorBody;
  if (typeof p.message === 'string' && p.message) return p.message;
  if (typeof p.error === 'string' && p.error) return p.error;
  return undefined;
};

/**
 * Map an HTTP status + payload to a friendly description. Accepts `undefined`
 * status to represent the "network failed entirely" case.
 */
export const mapOcppApiError = (
  status: number | undefined,
  payload: unknown
): OcppApiErrorInfo => {
  const explicit = readMessage(payload);
  if (status === undefined) {
    return {
      status,
      title: 'Network error',
      message: explicit ?? 'Could not reach the server. Check your connection and try again.',
      blockedByGate: false,
      featureGated: false,
    };
  }
  if (status === 400) {
    return {
      status,
      title: 'Invalid request',
      message: explicit ?? 'Some required information is missing or invalid.',
      blockedByGate: false,
      featureGated: false,
    };
  }
  if (status === 401) {
    return {
      status,
      title: 'Sign in again',
      message: explicit ?? 'Your session expired. Please sign in again.',
      blockedByGate: false,
      featureGated: false,
    };
  }
  if (status === 403) {
    return {
      status,
      title: 'Access denied',
      message: explicit ?? 'Only the charger host can access this view.',
      blockedByGate: false,
      featureGated: false,
    };
  }
  if (status === 404) {
    return {
      status,
      title: 'Not found',
      message: explicit ?? 'We could not find that charger or session.',
      blockedByGate: false,
      featureGated: false,
    };
  }
  if (status === 409) {
    if (isGateBlock(payload)) {
      return {
        status,
        title: 'Action blocked',
        message: payload.gate.reason,
        blockedByGate: true,
        gate: payload.gate,
        featureGated: false,
      };
    }
    return {
      status,
      title: 'Action not allowed',
      message: explicit ?? 'This action conflicts with the charger\'s current state.',
      blockedByGate: false,
      featureGated: false,
    };
  }
  if (status === 502) {
    return {
      status,
      title: 'Charger refused',
      message: explicit ?? 'The charger declined the request. Try again or check the activity log.',
      blockedByGate: false,
      featureGated: false,
    };
  }
  if (status === 503) {
    return {
      status,
      title: 'Feature unavailable',
      message:
        explicit ??
        'This action is currently disabled by the operator. Contact support if you need it enabled.',
      blockedByGate: false,
      featureGated: true,
    };
  }
  if (status >= 500) {
    return {
      status,
      title: 'Server error',
      message: explicit ?? 'Something went wrong on our side. Please try again shortly.',
      blockedByGate: false,
      featureGated: false,
    };
  }
  return {
    status,
    title: 'Request failed',
    message: explicit ?? `Unexpected response (HTTP ${status}).`,
    blockedByGate: false,
    featureGated: false,
  };
};

/** True if the status code represents a 2xx success. */
export const isOcppSuccess = (status: number | undefined): boolean =>
  typeof status === 'number' && status >= 200 && status < 300;
